import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SelectChangeEvent,
  Skeleton,
} from '@mui/material'
import { RetryMode, useDocumentTypes, useFinalizeDocument, useRetryDocument } from '@src/api/document-api'
import { useDeletePages, useReorderPages, useRotatePage, useUpdatePageTypes } from '@src/api/document-pages-api'
import { DocsAutomation, EProcessingStatus } from '@src/types/automationDocument'
import { DocumentPage, DocumentType, DocumentWithPages } from '@src/types/documentPage'
import { enqueueSnackbar } from 'notistack'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DocumentTypes, FA_CLASSIFICATION_MAPPING, IFP_CLASSIFICATION_MAPPING } from '@src/types/constants'
import { ApplicantFilter } from './coApplicantDocType'
import { ChangeDocumentTypeDialog } from './components/ChangeDocumentTypeDialog'
import { FinalizeConfirmationDialog } from './components/FinalizeConfirmationDialog'
import { PageZoomDialog } from './components/PageZoomDialog'
import { PagesToolbar } from './components/PagesToolbar'
import { SortablePageCard } from './components/SortablePageCard'
import { useStagedPages } from './hooks/useStagedPages'

interface PagesViewProps {
  pages: DocumentPage[]
  document: DocsAutomation
  viewOnly?: boolean
  nextDocumentUrl?: string
  canGoNext: boolean
  onDocTypeChange?: (docType: string) => void
  onOpenLinkDialog: () => void
}

const PagesView = ({ pages, document, viewOnly, nextDocumentUrl, canGoNext, onDocTypeChange, onOpenLinkDialog }: PagesViewProps) => {
  const { t, i18n } = useTranslation()
  const [columns, setColumns] = useState<number>(4)
  const [selectedPage, setSelectedPage] = useState<DocumentPage | null>(null)
  const [docTypeFilter, setDocTypeFilter] = useState<string>('all')
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set())
  const [changeTypeDialogOpen, setChangeTypeDialogOpen] = useState(false)
  const [documentTypeIdToAssign, setDocumentTypeIdToAssign] = useState<string>('')
  const [docTypeSearch, setDocTypeSearch] = useState<string>('')
  const [applicantFilter, setApplicantFilter] = useState<ApplicantFilter>('all')
  const [showDiscarded, setShowDiscarded] = useState<boolean>(false)
  const [finalizeDialogState, setFinalizeDialogState] = useState<'toolbar' | 'lastPage' | 'closed'>('closed')
  const [excludedDocumentTypeIds, setExcludedDocumentTypeIds] = useState<number[]>([])
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const staged = useStagedPages(pages)
  const { current, isDirty, canUndo, canRedo, acceptServerPages, computeDiff } = staged

  const lastSyncedPagesRef = useRef(pages)
  useEffect(() => {
    if (pages === lastSyncedPagesRef.current) return
    if (isDirty) return
    lastSyncedPagesRef.current = pages
    acceptServerPages(pages)
  }, [pages, isDirty, acceptServerPages])

  // Reset local state when navigating between documents
  useEffect(() => {
    setSelectedPage(null)
    setDocTypeFilter('all')
    setIsEditMode(false)
    setSelectedPages(new Set())
    setChangeTypeDialogOpen(false)
    setDocumentTypeIdToAssign('')
    setDocTypeSearch('')
    setShowDiscarded(false)
    setExcludedDocumentTypeIds([])
    setFinalizeDialogState('closed')
    setDiscardDialogOpen(false)
  }, [document.id])

  // Default the document-type filter to applicant-only when the linked application has no
  // co-applicant; otherwise show both. Re-evaluated when the link (and thus co-applicant) changes.
  const linkedHasCoApplicant = document.linkedCreditApp?.hasCoApplicant ?? false
  useEffect(() => {
    setApplicantFilter(linkedHasCoApplicant ? 'all' : 'applicant')
  }, [document.id, linkedHasCoApplicant])

  // Keep selectedPage in sync with staged state (e.g. after undo/redo/typeChange)
  useEffect(() => {
    if (!selectedPage) return
    const fresh = current.find((p) => p.id === selectedPage.id)
    if (fresh && fresh !== selectedPage) {
      setSelectedPage(fresh)
    }
  }, [current, selectedPage])

  const [documentTypesData] = useDocumentTypes()
  const updatePageTypesMutation = useUpdatePageTypes(document.id || '')
  const deletePagesMutation = useDeletePages(document.id || '')
  const reorderPagesMutation = useReorderPages(document.id || '')
  const rotatePageMutation = useRotatePage(document.id || '')
  const { mutateAsync: finalizeDocument, isPending: isFinalizing } = useFinalizeDocument()
  const { mutateAsync: retryDocument, isPending: isRetrying } = useRetryDocument()

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const getLatestVersionTag = useCallback(() => {
    const cached = queryClient.getQueryData<DocumentWithPages>(['fullDocument', document.id])
    return cached?.document.versionTag ?? document.versionTag
  }, [queryClient, document.id, document.versionTag])

  // Serialize page mutations so rapid clicks don't cause 409 conflicts.
  const mutationChainRef = useRef<Promise<unknown>>(Promise.resolve())
  const queueMutation = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const promise = mutationChainRef.current.then(fn, fn)
    mutationChainRef.current = promise.catch(() => {})
    return promise
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const successSnackbar = useCallback(() => {
    enqueueSnackbar(t('common.messages.success'), { variant: 'success' })
  }, [t])

  const errorSnackbar = useCallback(() => {
    enqueueSnackbar(t('common.messages.error'), { variant: 'error' })
  }, [t])

  const assignDocumentTypeIsOverridden = useCallback(
    (page: DocumentPage): boolean => {
      if (document.linkedCreditApp && page.documentTypeId && documentTypesData) {
        const productLine = document.linkedCreditApp.productLine
        let docType: number | undefined

        if (productLine === 0) {
          docType = FA_CLASSIFICATION_MAPPING[page.llmClassification ?? DocumentTypes.Other]
        } else if (productLine === 1) {
          docType = IFP_CLASSIFICATION_MAPPING[page.llmClassification ?? DocumentTypes.Other]
        }

        if (docType) {
          return docType !== Number(page.documentTypeId)
        }
      }

      return false
    },
    [document.linkedCreditApp, documentTypesData],
  )

  const assignDocumentTypeToPage = useCallback(
    (page: DocumentPage): string | null => {
      if (document.linkedCreditApp && page.documentTypeId && documentTypesData) {
        const productLine = document.linkedCreditApp.productLine
        let docType: DocumentType | undefined

        if (productLine === 0) {
          docType = documentTypesData.fa?.find((dt) => dt.id == page.documentTypeId)
        } else if (productLine === 1) {
          docType = documentTypesData.ifp?.find((dt) => dt.id == page.documentTypeId)
        }

        if (docType) {
          return i18n.language === 'fr' ? docType.nameFr : docType.nameEn
        }
      }

      return null
    },
    [document.linkedCreditApp, documentTypesData, i18n.language],
  )

  const pageDocumentClassificationType = useCallback(
    (page: DocumentPage) => {
      if (page.processingStatus === EProcessingStatus.Discarded) {
        return t('common.deleted' as 'common.close')
      }

      if (page.qrCodeDetectionResult?.isContractPage) {
        return 'Contract'
      }

      return page.llmClassification ?? 'Other'
    },
    [t],
  )

  const visiblePages = useMemo(() => {
    const filtered = showDiscarded ? current : current.filter((page) => page.processingStatus !== EProcessingStatus.Discarded)
    return [...filtered].sort((a, b) => {
      const aPageNum = a.pageNumber ?? 0
      const bPageNum = b.pageNumber ?? 0
      if (aPageNum !== bPageNum) return aPageNum - bPageNum
      return a.originalFilePageNumber - b.originalFilePageNumber
    })
  }, [current, showDiscarded])

  const documentTypes = useMemo(() => {
    const types = new Set<string>()
    visiblePages.forEach((page) => {
      types.add(pageDocumentClassificationType(page).trim())
    })
    return Array.from(types).sort()
  }, [pageDocumentClassificationType, visiblePages])

  const sentDocumentTypeIds = useMemo(() => document.sentDocumentTypeIds ?? [], [document.sentDocumentTypeIds])

  const sentPageIds = useMemo(() => {
    if (sentDocumentTypeIds.length === 0) return new Set<string>()
    const pl = document.linkedCreditApp?.productLine
    const sentSet = new Set(sentDocumentTypeIds)
    const ids = new Set<string>()
    for (const p of current) {
      if (p.processingStatus === EProcessingStatus.Discarded) continue
      let typeId: number | undefined
      if (p.documentTypeId != null) {
        typeId = Number(p.documentTypeId)
      } else {
        const classification = p.llmClassification ?? DocumentTypes.Other
        if (pl === 0) typeId = FA_CLASSIFICATION_MAPPING[classification]
        else if (pl === 1) typeId = IFP_CLASSIFICATION_MAPPING[classification]
      }
      if (typeId != null && sentSet.has(typeId)) ids.add(p.id)
    }
    return ids
  }, [current, sentDocumentTypeIds, document.linkedCreditApp?.productLine])

  const filteredPages = useMemo(() => {
    let result = visiblePages

    if (isEditMode && sentPageIds.size > 0) {
      result = result.filter((p) => !sentPageIds.has(p.id))
    }

    if (docTypeFilter === 'all') return result

    return result.filter((page) => pageDocumentClassificationType(page).trim() === docTypeFilter)
  }, [visiblePages, docTypeFilter, pageDocumentClassificationType, isEditMode, sentPageIds])

  const pageIndex = useCallback(
    (page: DocumentPage) => {
      const pageOfType = filteredPages.filter(
        (p) => pageDocumentClassificationType(p) === pageDocumentClassificationType(page),
      )
      return pageOfType.findIndex((p) => p.id === page.id)
    },
    [filteredPages, pageDocumentClassificationType],
  )

  const pageHeader = useCallback(
    (page: DocumentPage) => {
      const type = pageDocumentClassificationType(page)
      const index = pageIndex(page) + 1

      const confidence =
        page.llmClassification != null ? `${((page.llmConfidenceScore ?? 0) * 100).toFixed(0)}%` : 'N/A'

      return `${type} - ${index} (${confidence})`
    },
    [pageDocumentClassificationType, pageIndex],
  )

  const handleColumnsChange = (_event: React.MouseEvent<HTMLElement>, newColumns: number | null) => {
    if (newColumns !== null) {
      setColumns(newColumns)
    }
  }

  const handleDocTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value
    setDocTypeFilter(newType)
    if (onDocTypeChange) {
      onDocTypeChange(newType === 'all' ? '' : newType)
    }
  }

  const handlePageClick = (page: DocumentPage) => {
    setSelectedPage(page)
  }

  const nextPage = (pageId: string) => {
    const currentIndex = filteredPages.findIndex((p) => p.id === pageId)
    if (currentIndex >= 0 && currentIndex < filteredPages.length - 1) {
      setSelectedPage(filteredPages[currentIndex + 1])
    } else if (currentIndex === filteredPages.length - 1) {
      setFinalizeDialogState('lastPage')
    }
  }

  const handleZoomChangeDocType = (page: DocumentPage, documentTypeId?: string) => {
    if (!document.linkedCreditApp) {
      enqueueSnackbar(t('common.mustLinkToApplication'), { variant: 'warning' })
      return
    }
    if (documentTypeId) {
      staged.changeTypes([page.id], Number(documentTypeId))
      nextPage(page.id)
    } else {
      setSelectedPages(new Set([page.id]))
      setChangeTypeDialogOpen(true)
    }
  }

  const handleZoomDelete = (page: DocumentPage) => {
    staged.deletePages([page.id])
    nextPage(page.id)
  }

  const handleZoomUndo = () => {
    const { affectedPageIds, restoredPages } = staged.undo()
    if (affectedPageIds.length === 0) return
    const target = restoredPages.find((p) => affectedPageIds.includes(p.id))
    if (target) setSelectedPage(target)
  }

  const handleZoomRedo = () => {
    const { affectedPageIds, restoredPages } = staged.redo()
    if (affectedPageIds.length === 0) return
    const target = restoredPages.find((p) => affectedPageIds.includes(p.id))
    if (target) setSelectedPage(target)
  }

  const handleToggleEditMode = () => {
    if (isEditMode && isDirty) {
      setDiscardDialogOpen(true)
      return
    }
    setIsEditMode(!isEditMode)
    setSelectedPages(new Set())
  }

  const handleConfirmDiscard = () => {
    staged.undoAll()
    setDiscardDialogOpen(false)
    setIsEditMode(false)
    setSelectedPages(new Set())
  }

  const handleCancelDiscard = () => {
    setDiscardDialogOpen(false)
  }

  const handleSave = async () => {
    if (!isDirty || isSaving) return
    const diff = computeDiff()

    setIsSaving(true)
    try {
      await queueMutation(async () => {
        if (diff.deletedIds.length > 0) {
          await deletePagesMutation.mutateAsync({
            pageIds: diff.deletedIds,
            versionTag: getLatestVersionTag(),
          })
        }
        for (const [typeId, ids] of diff.typeChangesByTypeId) {
          await updatePageTypesMutation.mutateAsync({
            pageIds: ids,
            documentTypeId: typeId,
            versionTag: getLatestVersionTag(),
          })
        }
        for (const { id, rotation } of diff.rotations) {
          await rotatePageMutation.mutateAsync({
            pageId: id,
            payload: { pageId: id, rotation, versionTag: getLatestVersionTag() },
          })
        }
        if (diff.reorders.length > 0) {
          await reorderPagesMutation.mutateAsync({
            pages: diff.reorders,
            versionTag: getLatestVersionTag(),
          })
        }
      })

      const cached = queryClient.getQueryData<DocumentWithPages>(['fullDocument', document.id])
      if (cached?.documentPages) {
        lastSyncedPagesRef.current = cached.documentPages
        acceptServerPages(cached.documentPages)
      }

      successSnackbar()
    } catch {
      errorSnackbar()
    } finally {
      setIsSaving(false)
    }
  }

  const handleFinalize = () => {
    if (!document.id || !document.versionTag) return

    void queueMutation(() =>
      finalizeDocument({
        documentId: document.id,
        versionTag: getLatestVersionTag(),
        excludedDocumentTypeIds: excludedDocumentTypeIds.length > 0 ? excludedDocumentTypeIds : undefined,
      }),
    ).then((result) => {
      if (result.processingStatus === EProcessingStatus.PartiallySent) {
        enqueueSnackbar(t('docsAutomation.partialFinalizationWarning' as 'common.close'), { variant: 'warning', autoHideDuration: 10000 })
      } else {
        successSnackbar()
        if (canGoNext && nextDocumentUrl) {
          navigate(nextDocumentUrl)
        } else {
          const savedUrl = sessionStorage.getItem('documentsListUrl')
          navigate(savedUrl || '/documents')
        }
      }
    })
  }

  const handleRetry = async (mode: RetryMode) => {
    if (!document.id || !document.versionTag) return
    try {
      await retryDocument({
        documentId: document.id,
        versionTag: getLatestVersionTag(),
        mode,
      })
      enqueueSnackbar(t('docsAutomation.retryStarted' as 'common.close'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('common.error' as 'common.close'), { variant: 'error' })
    }
  }

  const handleConfirmFinalize = () => {
    setFinalizeDialogState('closed')
    setSelectedPage(null)
    handleFinalize()
  }

  const handleCancelFinalize = () => {
    setFinalizeDialogState('closed')
    setExcludedDocumentTypeIds([])
  }

  const handleTogglePageSelection = (pageId: string) => {
    const newSelected = new Set(selectedPages)
    if (newSelected.has(pageId)) {
      newSelected.delete(pageId)
    } else {
      newSelected.add(pageId)
    }
    setSelectedPages(newSelected)
  }

  const handleDeleteSelected = () => {
    if (selectedPages.size === 0) return
    staged.deletePages(Array.from(selectedPages))
    setSelectedPages(new Set())
  }

  const handleRotatePage = (pageId: string, degrees: number) => {
    staged.rotatePage(pageId, degrees)
  }

  const handleOpenChangeTypeDialog = () => {
    if (!document.linkedCreditApp) {
      enqueueSnackbar(t('common.mustLinkToApplication'), { variant: 'warning' })
      return
    }
    setChangeTypeDialogOpen(true)
  }

  const handleCloseChangeTypeDialog = () => {
    setChangeTypeDialogOpen(false)
    setDocumentTypeIdToAssign('')
    setDocTypeSearch('')
  }

  const handleConfirmChangeType = () => {
    if (!documentTypeIdToAssign || selectedPages.size === 0) return

    staged.changeTypes(Array.from(selectedPages), Number(documentTypeIdToAssign))
    handleCloseChangeTypeDialog()
    const pageIds = Array.from(selectedPages)
    setSelectedPages(new Set())

    if (selectedPage && pageIds.includes(selectedPage.id)) {
      nextPage(selectedPage.id)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id || docTypeFilter === 'all') {
      return
    }

    const oldIndexFiltered = filteredPages.findIndex((p) => p.id === active.id)
    const newIndexFiltered = filteredPages.findIndex((p) => p.id === over.id)

    if (oldIndexFiltered === -1 || newIndexFiltered === -1) return

    const newFilteredPages = [...filteredPages]
    const [movedPage] = newFilteredPages.splice(oldIndexFiltered, 1)
    newFilteredPages.splice(newIndexFiltered, 0, movedPage)

    staged.reorder(newFilteredPages, new Set(filteredPages.map((p) => p.id)))
  }

  const PROCESSING_STATUSES = new Set([
    EProcessingStatus.Pending,
    EProcessingStatus.PendingBatch,
    EProcessingStatus.Stored,
    EProcessingStatus.Processing,
    EProcessingStatus.Classified,
    EProcessingStatus.Extracted,
  ])

  const isProcessing = PROCESSING_STATUSES.has(document.processingStatus)
  const showSkeletons = filteredPages.length === 0 && isProcessing

  const getGridWidth = () => {
    switch (columns) {
      case 1:
        return '100%'
      case 2:
        return 'calc(50% - 8px)'
      case 3:
        return 'calc(33.333% - 10.67px)'
      case 4:
        return 'calc(25% - 12px)'
      default:
        return 'calc(25% - 12px)'
    }
  }

  const productLine = document.linkedCreditApp?.productLine

  const pageCountByTypeId = useMemo(() => {
    const counts = new Map<number, number>()
    for (const p of current) {
      if (p.processingStatus === EProcessingStatus.Discarded) continue
      let typeId: number | undefined
      if (p.documentTypeId != null) {
        typeId = Number(p.documentTypeId)
      } else {
        const classification = p.llmClassification ?? DocumentTypes.Other
        if (productLine === 0) typeId = FA_CLASSIFICATION_MAPPING[classification]
        else if (productLine === 1) typeId = IFP_CLASSIFICATION_MAPPING[classification]
      }
      if (typeId != null) counts.set(typeId, (counts.get(typeId) ?? 0) + 1)
    }
    return counts
  }, [current, productLine])

  const pageDocumentTypeIds = useMemo(() => {
    return [...pageCountByTypeId.keys()]
  }, [pageCountByTypeId])

  const pageDocumentTypeNames = useMemo(() => {
    const map = new Map<number, string>()
    if (!documentTypesData) return map
    const types = productLine === 0 ? documentTypesData.fa : productLine === 1 ? documentTypesData.ifp : []
    for (const dt of types ?? []) {
      map.set(dt.id, i18n.language === 'fr' ? dt.nameFr : dt.nameEn)
    }
    return map
  }, [documentTypesData, productLine, i18n.language])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PagesToolbar
        document={document}
        documentTypes={documentTypes}
        viewOnly={viewOnly}
        isEditMode={isEditMode}
        selectedPages={selectedPages}
        sentPageIds={sentPageIds}
        selectedDocType={docTypeFilter}
        showDiscarded={showDiscarded}
        columns={columns}
        allPages={current}
        visiblePages={visiblePages}
        isDeletePending={false}
        isFinalizing={isFinalizing}
        canUndo={canUndo}
        canRedo={canRedo}
        canUndoAll={isDirty}
        canSave={isDirty}
        isSaving={isSaving}
        isDirty={isDirty}
        onUndo={staged.undo}
        onRedo={staged.redo}
        onUndoAll={staged.undoAll}
        onSave={handleSave}
        onToggleEditMode={handleToggleEditMode}
        onFinalize={() => setFinalizeDialogState('toolbar')}
        onDeleteSelected={handleDeleteSelected}
        onOpenChangeTypeDialog={handleOpenChangeTypeDialog}
        onDeselectAll={() => setSelectedPages(new Set())}
        onDocTypeChange={handleDocTypeChange}
        onShowDiscardedChange={setShowDiscarded}
        onColumnsChange={handleColumnsChange}
        pageHeader={pageHeader}
        onOpenLinkDialog={onOpenLinkDialog}
        onRetry={handleRetry}
        isRetrying={isRetrying}
      />

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {showSkeletons ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 2 }}>
          {Array.from({ length: 3 }, (_, i) => (
            <Box key={i} sx={{ width: getGridWidth() }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width="60%" sx={{ mt: 0.5 }} />
            </Box>
          ))}
        </Box>
      ) : (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={filteredPages.map((p) => p.id)}
          strategy={rectSortingStrategy}
          disabled={!isEditMode || docTypeFilter === 'all'}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {filteredPages.map((page) => (
              <Box key={page.id} sx={{ width: getGridWidth(), position: 'relative' }}>
                <SortablePageCard
                  page={page}
                  isEditMode={isEditMode}
                  isDragDisabled={docTypeFilter === 'all'}
                  isSelected={selectedPages.has(page.id)}
                  isSent={sentPageIds.has(page.id)}
                  columns={columns}
                  onToggleSelection={handleTogglePageSelection}
                  onRotate={handleRotatePage}
                  onClick={handlePageClick}
                  pageHeader={pageHeader}
                  assignDocumentTypeToPage={assignDocumentTypeToPage}
                  assignDocumentTypeIsOverridden={assignDocumentTypeIsOverridden}
                />
              </Box>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      )}
      </Box>

      <PageZoomDialog
        selectedPage={selectedPage}
        filteredPages={filteredPages}
        pageHeader={pageHeader}
        assignDocumentTypeToPage={assignDocumentTypeToPage}
        assignDocumentTypeIsOverridden={assignDocumentTypeIsOverridden}
        onClose={() => setSelectedPage(null)}
        onNavigate={setSelectedPage}
        canEdit={!viewOnly && !isFinalizing}
        sentPageIds={sentPageIds}
        onChangeDocType={handleZoomChangeDocType}
        onDelete={handleZoomDelete}
        onRotate={handleRotatePage}
        productLine={productLine}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={handleZoomUndo}
        onRedo={handleZoomRedo}
      />

      <ChangeDocumentTypeDialog
        open={changeTypeDialogOpen}
        selectedPagesCount={selectedPages.size}
        documentTypesData={documentTypesData}
        productLine={productLine}
        selectedDocumentType={documentTypeIdToAssign}
        docTypeSearch={docTypeSearch}
        applicantFilter={applicantFilter}
        onApplicantFilterChange={setApplicantFilter}
        onClose={handleCloseChangeTypeDialog}
        onConfirm={handleConfirmChangeType}
        onReset={() => setDocumentTypeIdToAssign('')}
        onSelectType={setDocumentTypeIdToAssign}
        onSearchChange={setDocTypeSearch}
        isLoading={false}
      />

      <FinalizeConfirmationDialog
        open={finalizeDialogState !== 'closed'}
        finalizeDialogState={finalizeDialogState}
        linkedCreditApp={document.linkedCreditApp}
        documentId={document.id}
        isFinalizing={isFinalizing}
        pageDocumentTypeIds={pageDocumentTypeIds}
        pageDocumentTypeNames={pageDocumentTypeNames}
        pageCountByTypeId={pageCountByTypeId}
        excludedDocumentTypeIds={excludedDocumentTypeIds}
        sentDocumentTypeIds={sentDocumentTypeIds}
        onExcludedTypeIdsChange={setExcludedDocumentTypeIds}
        onConfirm={handleConfirmFinalize}
        onCancel={handleCancelFinalize}
      />

      <Dialog open={discardDialogOpen} onClose={handleCancelDiscard}>
        <DialogTitle>{t('docsAutomation.unsavedChangesTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('docsAutomation.unsavedChangesBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscard}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDiscard} color="error" variant="contained">
            {t('docsAutomation.discardChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PagesView
